// src/lib/composables/useAppointmentSummaryRows.ts
import { computed, type Ref } from 'vue';

import type { MpfSummaryListRow } from '../components/MpfSummaryList/MpfSummaryList.props';
import type { AppointmentSummary } from '../../types/appointment';
import { formatDateTime } from '../helpers/dates';

// Keyed on the wire values of the backend's DeliveryMode enum, with its own
// patient-facing labels.
const DELIVERY_MODE_LABELS: Record<string, string> = {
  'face-to-face': 'Face-to-face',
  'home-visit': 'Home visit',
  video: 'Video consultation',
  phone: 'Phone consultation',
};

export function deliveryModeLabel(deliveryMode: string): string {
  // An unmapped mode is shown as-is rather than hidden — the patient needs to
  // know how they are being seen, even if the wording is unpolished.
  return DELIVERY_MODE_LABELS[deliveryMode] ?? deliveryMode;
}

// One summary shape for all three screens. `location` and `practitioner` are
// optional, and an absent one is dropped rather than shown empty.
export function useAppointmentSummaryRows(appointment: Ref<AppointmentSummary | null>) {
  const summaryRows = computed<MpfSummaryListRow[]>(() => {
    if (!appointment.value) return [];

    const rows: MpfSummaryListRow[] = [
      { key: 'Date and time', value: formatDateTime(appointment.value.dateTime) },
      { key: 'Appointment type', value: deliveryModeLabel(appointment.value.deliveryMode) },
    ];

    if (appointment.value.location) {
      rows.push({ key: 'Location', value: appointment.value.location });
    }

    if (appointment.value.practitioner) {
      rows.push({ key: 'Practitioner', value: appointment.value.practitioner });
    }

    return rows;
  });

  return { summaryRows };
}
